import { SafeStorage } from "../storage/storage";
import { generateSessionId } from "../identity/anonymous";
import { CookieOptions } from "../storage/cookie";
import { SessionInfo } from "../core/types";

export class SessionManager {
  private static SESSION_KEY = "__thrico_session_id";
  private static LAST_ACT_KEY = "__thrico_last_act";
  private static START_TIME_KEY = "__thrico_session_start";

  private currentSessionId: string;
  private timeoutMinutes: number;
  private cookieOptions: CookieOptions;
  private isNewSession: boolean = false;
  private sessionStartedAt: number;

  constructor(timeoutMinutes: number = 30, cookieOptions: CookieOptions = {}) {
    this.timeoutMinutes = timeoutMinutes;
    this.cookieOptions = cookieOptions;

    const now = Date.now();
    const existingSession = SafeStorage.getItem(SessionManager.SESSION_KEY);
    const lastActivityStr = SafeStorage.getItem(SessionManager.LAST_ACT_KEY);
    const lastActivity = lastActivityStr ? parseInt(lastActivityStr, 10) : 0;
    const timeoutMs = this.timeoutMinutes * 60 * 1000;

    if (existingSession && lastActivity && now - lastActivity < timeoutMs) {
      // Continue existing active session
      this.currentSessionId = existingSession;
      this.isNewSession = false;
      const startStr = SafeStorage.getItem(SessionManager.START_TIME_KEY);
      this.sessionStartedAt = startStr ? parseInt(startStr, 10) : now;
    } else {
      // Start brand new session
      this.currentSessionId = generateSessionId();
      this.isNewSession = true;
      this.sessionStartedAt = now;

      SafeStorage.setItem(SessionManager.SESSION_KEY, this.currentSessionId, this.cookieOptions);
      SafeStorage.setItem(SessionManager.START_TIME_KEY, String(now), this.cookieOptions);
    }

    this.touch();
  }

  public getSessionInfo(): SessionInfo {
    return {
      sessionId: this.currentSessionId,
      isNew: this.isNewSession,
      startedAt: this.sessionStartedAt,
      lastActivityAt: Date.now(),
    };
  }

  public getSessionId(): string {
    return this.currentSessionId;
  }

  public isNew(): boolean {
    return this.isNewSession;
  }

  public markAsEstablished(): void {
    this.isNewSession = false;
  }

  /**
   * Update the last activity timestamp
   */
  public touch(): void {
    const now = Date.now();
    const timeoutMs = this.timeoutMinutes * 60 * 1000;
    const lastActivityStr = SafeStorage.getItem(SessionManager.LAST_ACT_KEY);
    const lastActivity = lastActivityStr ? parseInt(lastActivityStr, 10) : 0;

    if (lastActivity && now - lastActivity >= timeoutMs) {
      // Session timed out during tab activity -> rotate session ID
      this.currentSessionId = generateSessionId();
      this.isNewSession = true;
      this.sessionStartedAt = now;
      SafeStorage.setItem(SessionManager.SESSION_KEY, this.currentSessionId, this.cookieOptions);
      SafeStorage.setItem(SessionManager.START_TIME_KEY, String(now), this.cookieOptions);
    }

    SafeStorage.setItem(SessionManager.LAST_ACT_KEY, String(now), this.cookieOptions);
  }

  public reset(): void {
    SafeStorage.removeItem(SessionManager.SESSION_KEY, this.cookieOptions);
    SafeStorage.removeItem(SessionManager.LAST_ACT_KEY, this.cookieOptions);
    SafeStorage.removeItem(SessionManager.START_TIME_KEY, this.cookieOptions);
    this.currentSessionId = generateSessionId();
    this.isNewSession = true;
    this.sessionStartedAt = Date.now();
    SafeStorage.setItem(SessionManager.SESSION_KEY, this.currentSessionId, this.cookieOptions);
    SafeStorage.setItem(SessionManager.START_TIME_KEY, String(this.sessionStartedAt), this.cookieOptions);
    this.touch();
  }
}
